import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { cn } from '@/shared/utils';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  X,
  Bell,
  BellOff,
  Check,
  Thermometer,
  Package,
  Calendar,
  ShoppingCart,
  Wifi,
  WifiOff,
  Loader2 } from
'lucide-react';

const API_URL = API_BASE;

const alertTypeConfig = {
  critical: {
    icon: AlertCircle,
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-200 dark:border-red-800',
    iconColor: 'text-red-600',
    badge: 'bg-red-100 text-red-700 border-red-200'
  },
  warning: {
    icon: AlertTriangle,
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    border: 'border-amber-200 dark:border-amber-800',
    iconColor: 'text-amber-600',
    badge: 'bg-amber-100 text-amber-700 border-amber-200'
  },
  info: {
    icon: Info,
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    border: 'border-blue-200 dark:border-blue-800',
    iconColor: 'text-blue-600',
    badge: 'bg-blue-100 text-blue-700 border-blue-200'
  }
};

const categoryIcons = {
  low_stock: Package,
  temperature: Thermometer,
  expiry: Calendar,
  reorder: ShoppingCart
};

// Default empty alerts
const defaultAlerts = [];

export const RealTimeAlertPanel = ({
  maxAlerts = 5,
  showHeader = true,
  compact = false,
  className
}) => {const { t } = useTranslation("wms");
  const [alerts, setAlerts] = useState(defaultAlerts);
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(true);
  const [isMuted, setIsMuted] = useState(false);

  // Fetch alerts from API
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setLoading(true);
        // Try to fetch from temperature alerts or cold chain alerts
        const response = await fetch(`${API_URL}/api/coldchain/alerts?limit=10`);
        if (response.ok) {
          const data = await response.json();
          const transformedAlerts = data.map((a, idx) => ({
            id: a.id || `alert-${idx}`,
            type: a.severity === 'critical' ? 'critical' : a.severity === 'high' ? 'warning' : 'info',
            title: a.alert_type || 'Temperature Alert',
            message: a.message || `${a.sensor_name}: ${a.current_temp}°C (Threshold: ${a.threshold}°C)`,
            category: 'temperature',
            timestamp: a.triggered_at || new Date().toISOString(),
            acknowledged: a.acknowledged || false
          }));
          setAlerts(transformedAlerts);
        }
      } catch (err) {
        console.error('Failed to fetch alerts:', err);
        // Keep empty alerts on error
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleAcknowledge = (alertId) => {
    setAlerts((prev) =>
    prev.map((alert) =>
    alert.id === alertId ?
    { ...alert, acknowledged: true, acknowledgedBy: 'Current User' } :
    alert
    )
    );
  };

  const handleDismiss = (alertId) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
  };

  const unacknowledgedCount = alerts.filter((a) => !a.acknowledged).length;
  const criticalCount = alerts.filter((a) => a.type === 'critical' && !a.acknowledged).length;

  const displayedAlerts = alerts.slice(0, maxAlerts);

  if (compact) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <div className="relative">
          <Bell className={cn(
            "h-5 w-5",
            criticalCount > 0 ? "text-red-600 animate-pulse" : "text-muted-foreground"
          )} />
          {unacknowledgedCount > 0 &&
          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-600 text-white text-xs flex items-center justify-center">
              {unacknowledgedCount}
            </span>
          }
        </div>
        <div className="flex items-center gap-1.5">
          {isConnected ?
          <Wifi className="h-3.5 w-3.5 text-green-600" /> :

          <WifiOff className="h-3.5 w-3.5 text-red-600" />
          }
          <span className="text-xs text-muted-foreground">
            {isConnected ? 'Live' : 'Disconnected'}
          </span>
        </div>
      </div>);

  }

  return (
    <Card className={cn("card-elevated", className)} data-testid="realtime-alert-panel">
      {showHeader &&
      <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="relative">
                <Bell className={cn(
                "h-5 w-5",
                criticalCount > 0 && "text-red-600 animate-pulse"
              )} />
                {unacknowledgedCount > 0 &&
              <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-red-600 text-white text-[10px] flex items-center justify-center font-medium">
                    {unacknowledgedCount}
                  </span>
              }
              </div>{t("realtimeAlerts", "Real-Time Alerts")}

          </CardTitle>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 text-xs">
                {isConnected ?
              <>
                    <Wifi className="h-3.5 w-3.5 text-green-600" />
                    <span className="text-green-600">{t("connected", "Connected")}</span>
                  </> :

              <>
                    <WifiOff className="h-3.5 w-3.5 text-red-600" />
                    <span className="text-red-600">{t("disconnected", "Disconnected")}</span>
                  </>
              }
              </div>
              <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMuted(!isMuted)}
              className="h-8 w-8 p-0">
              
                {isMuted ?
              <BellOff className="h-4 w-4 text-muted-foreground" /> :

              <Bell className="h-4 w-4" />
              }
              </Button>
            </div>
          </div>
        </CardHeader>
      }
      <CardContent className={cn(showHeader ? "pt-0" : "pt-4")}>
        {displayedAlerts.length > 0 ?
        <div className="space-y-2">
            {displayedAlerts.map((alert) => {
            const config = alertTypeConfig[alert.type];
            const Icon = config.icon;
            const CategoryIcon = categoryIcons[alert.category] || Package;

            return (
              <div
                key={alert.id}
                className={cn(
                  "p-3 rounded-lg border transition-all",
                  config.bg,
                  config.border,
                  alert.acknowledged && "opacity-60"
                )}>
                
                  <div className="flex items-start gap-3">
                    <div className={cn("mt-0.5", config.iconColor)}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{alert.title}</span>
                        <Badge className={cn("text-[10px] px-1.5 py-0", config.badge)}>
                          {alert.type.toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {alert.message}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <CategoryIcon className="h-3 w-3" />
                          {alert.category.replace('_', ' ')}
                        </span>
                        <span>
                          {new Date(alert.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {alert.acknowledged &&
                      <span className="flex items-center gap-1 text-green-600">
                            <Check className="h-3 w-3" />{t("acknowledged", "Acknowledged")}

                      </span>
                      }
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {!alert.acknowledged &&
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleAcknowledge(alert.id)}
                      className="h-7 px-2 text-xs">
                      
                          <Check className="h-3.5 w-3.5 mr-1" />{t("ack", "Ack")}

                    </Button>
                    }
                      <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDismiss(alert.id)}
                      className="h-7 w-7 p-0">
                      
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>);

          })}
          </div> :

        <div className="text-center py-6 text-muted-foreground">
            <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">{t("noActiveAlerts", "No active alerts")}</p>
          </div>
        }
        
        {alerts.length > maxAlerts &&
        <Button variant="ghost" className="w-full mt-2 text-sm">{t("viewAll", "View all")}
          {alerts.length}{t("alerts", "alerts")}
        </Button>
        }
      </CardContent>
    </Card>);

};

// Floating alert badge for header/navbar
export const AlertBadge = ({ onClick }) => {const { t } = useTranslation("wms");
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/coldchain/alerts?limit=10`);
        if (response.ok) {
          const data = await response.json();
          setAlerts(data.map((a) => ({
            ...a,
            type: a.severity === 'critical' ? 'critical' : a.severity === 'high' ? 'warning' : 'info',
            acknowledged: a.acknowledged || false
          })));
        }
      } catch (err) {
        console.error('Failed to fetch alerts:', err);
      }
    };
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const criticalCount = alerts.filter((a) => a.type === 'critical' && !a.acknowledged).length;
  const totalUnread = alerts.filter((a) => !a.acknowledged).length;

  return (
    <button
      onClick={onClick}
      className="relative p-2 rounded-lg hover:bg-muted transition-colors"
      data-testid="alert-badge">
      
      <Bell className={cn(
        "h-5 w-5",
        criticalCount > 0 ? "text-red-600 animate-pulse" : "text-muted-foreground"
      )} />
      {totalUnread > 0 &&
      <span className={cn(
        "absolute top-0.5 right-0.5 h-4 w-4 rounded-full text-white text-[10px] flex items-center justify-center font-medium",
        criticalCount > 0 ? "bg-red-600" : "bg-amber-500"
      )}>
          {totalUnread}
        </span>
      }
    </button>);

};